import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { Box, Button, IconButton, Popover, Tooltip } from "@mui/material";
import { DateCalendar } from "@mui/x-date-pickers";
import type { Moment } from "moment-timezone";
import { type MouseEvent, useState } from "react";

export type PeriodNavigatorView = "year" | "month" | "day";

interface PeriodNavigatorProps {
	label: string;
	value: Moment;
	views: PeriodNavigatorView[];
	maxDate?: Moment;
	nextDisabled: boolean;
	onPrevious: () => void;
	onNext: () => void;
	onCurrent: () => void;
	onChange: (value: Date) => void;
}

export const PeriodNavigator = ({
	label,
	value,
	views,
	maxDate,
	nextDisabled,
	onPrevious,
	onNext,
	onCurrent,
	onChange,
}: PeriodNavigatorProps) => {
	const [calendarAnchor, setCalendarAnchor] = useState<HTMLElement | null>(
		null,
	);
	const openCalendar = (event: MouseEvent<HTMLElement>) =>
		setCalendarAnchor(event.currentTarget);
	const closeCalendar = () => setCalendarAnchor(null);

	return (
		<Box sx={{ alignItems: "center", display: "flex", gap: 0.75 }}>
			<Box
				sx={{
					alignItems: "center",
					border: 1,
					borderColor: "divider",
					borderRadius: 1,
					display: "flex",
					gap: 0.25,
					height: 40,
					px: 0.25,
					backgroundColor: "white",
				}}
			>
				<Tooltip title="Previous period">
					<IconButton
						size="small"
						aria-label="Previous period"
						onClick={onPrevious}
					>
						<ChevronLeft fontSize="small" />
					</IconButton>
				</Tooltip>
				<Button
					size="small"
					color="inherit"
					aria-haspopup="dialog"
					aria-label={`Change period, currently ${label}`}
					onClick={openCalendar}
					sx={{
						fontVariantNumeric: "tabular-nums",
						fontWeight: 600,
						minWidth: 150,
						textTransform: "none",
						backgroundColor: "white",
					}}
				>
					{label}
				</Button>
				<Tooltip title="Next period">
					<span>
						<IconButton
							size="small"
							aria-label="Next period"
							disabled={nextDisabled}
							onClick={onNext}
						>
							<ChevronRight fontSize="small" />
						</IconButton>
					</span>
				</Tooltip>
			</Box>
			<Button size="small" variant="text" onClick={onCurrent}>
				Current
			</Button>
			<Popover
				open={calendarAnchor !== null}
				anchorEl={calendarAnchor}
				onClose={closeCalendar}
				anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
			>
				<DateCalendar
					value={value}
					views={views}
					openTo={views[views.length - 1]}
					maxDate={maxDate}
					onChange={(selected: Moment | null) => {
						if (!selected?.isValid()) return;
						onChange(selected.toDate());
						closeCalendar();
					}}
				/>
			</Popover>
		</Box>
	);
};
